export const OPTIONAL_BUTTON_ACTIVATIONS = {
  // Slide 3: Are you currently taking any supplements? "Yes" → show frequency (4) and how many (5)
  '3_takeSupplements_Yes': [4, 5],
};

export const FOCUS_PRIORITY_SLIDE_CODE = 58;
export const GENDER_SLIDE_CODE = 6;
export const FEMALE_GENDER_VALUE = 'Female';
export const MENOPAUSE_FOCUS_AREA_NAME = 'Perimenopause-Menopause-Symptoms';
export const MENOPAUSE_FOCUS_AREA_ANSWER_CODE = 307;
export const MENOPAUSE_SYMPTOMS_SLIDE_CODE = 59;
export const MENOPAUSE_HRT_SLIDE_CODE = 60;
export const MENOPAUSE_FOLLOWUP_SLIDE_CODES = [MENOPAUSE_SYMPTOMS_SLIDE_CODE, MENOPAUSE_HRT_SLIDE_CODE];

export const FOCUS_AREA_ACTIVATED_SLIDE_CODES = {
  Energy: [13, 14], // How would you describe your energy levels? + At what time of day tired?
  Focus: [15, 16], // Trouble focusing? + Short-term memory
  'Sport-Performance': [17, 18], // Seeking to improve? + Preparing for competition?
  Mood: [19], // What would describe your mood?
  Sleep: [20, 21], // Trouble falling asleep? + Tired when you wake up?
  Stress: [22, 23], // Stress described as + When do you feel stressed?
  Digestion: [24], // What is affecting your digestion?
  'Immune-System': [25], // Do you get sick often?
  Skin: [26], // Main concerns about your skin?
  Hair: [27], // Main concerns about your hair?
  Nails: [28], // Main concerns regarding your nails?
  Heart: [29, 30], // Family history of heart problems? + Heart condition?
  Eyes: [31, 32], // Screen time? + Dry/red eyes?
  'Muscle-Pain': [33], // Which type of muscle pain?
  'Bones-and-Joints': [34, 35], // Bones/joints family? + Main concerns about bones and joints?
  'Perimenopause-Menopause-Symptoms': [59, 60], // Menopause/perimenopause symptoms + HRT
};

export function getOptionalButtonActivatedSlides(slideCode, name, value) {
  if (slideCode == null || !name || value == null) return [];
  const key = `${slideCode}_${name}_${value}`;
  return OPTIONAL_BUTTON_ACTIVATIONS[key] ?? [];
}

export function getFocusAreaActivatedSlides(name) {
  if (!name) return [];
  return FOCUS_AREA_ACTIVATED_SLIDE_CODES[name] ?? [];
}

export const MULTI_SELECT_SLIDE_CODES = new Set([
  16, // Improvement areas
  23, // Digestion issues
  25, // Skin concerns
  26, // Hair concerns
  27, // Nail concerns
  29, // Heart conditions
  32, // Muscle pain types
  33, // Bone problems
  34, // About bone problems
  36, // Diet types
  50, // Exercise types
  51, // Allergies
  54, // Medical conditions
  55, // How did you hear about us?
  59, // Menopause/perimenopause symptoms
]);

// Map slide code to group selector name
// Used for DOM manipulation in multi-select slides
export const SLIDE_GROUP_SELECTORS = {
  16: 'gImprovement',
  23: 'gDigestion',
  25: 'gSkin',
  26: 'gHair',
  27: 'gNails',
  29: 'gHeartCond',
  32: 'gMusclePain',
  33: 'gAnyBoneProb',
  34: 'gAboutBoneProb',
  36: 'gDiet',
  50: 'gExercise-type',
  51: 'gAllergies',
  54: 'gConditions',
  55: 'gHearAboutUs',
  59: 'gMenopauseSymptoms',
  60: 'gMenopauseHrt',
};

// Validation rules by slide code
// Each entry defines which validation function to use for a slide
export const VALIDATION_CONFIG = {
  1: { type: 'name', fields: ['name', 'agree'] },
  7: { type: 'birthdate', fields: ['day', 'month', 'year'] },
  8: { type: 'weight-height', fields: ['weight', 'height'] },
  9: { type: 'country', fields: ['country'] },
  11: { type: 'selection-required', groupName: 'gImprove' },
  12: { type: 'email', fields: ['email'] },
  58: { type: 'focus-priority' },
  24: { type: 'selection-required', groupName: 'gDigestion' },
  26: { type: 'selection-required', groupName: 'gSkin' },
  27: { type: 'selection-required', groupName: 'gHair' },
  28: { type: 'selection-required', groupName: 'gNails' },
  30: { type: 'selection-required', groupName: 'gHeartCond' },
  33: { type: 'selection-required', groupName: 'gMusclePain' },
  34: { type: 'selection-required', groupName: 'gAnyBoneProb' },
  35: { type: 'selection-required', groupName: 'gAboutBoneProb' },
  37: { type: 'selection-required', groupName: 'gDiet' },
  51: { type: 'selection-required', groupName: 'gExercise-type' },
  52: { type: 'selection-required', groupName: 'gAllergies' },
  55: { type: 'selection-required', groupName: 'gHearAboutUs' },
  59: { type: 'selection-required', groupName: 'gMenopauseSymptoms' },
  60: { type: 'selection-required', groupName: 'gMenopauseHrt' },
};

/**
 * Diet-related slide navigation logic (Slide Code 37)
 * Defines which slides to activate/deactivate and which values to auto-fill
 * based on dietary preferences
 * 
 * Slide Mapping (Index → Code):
 * - Index 36 → Code 37: Diet selection
 * - Index 37 → Code 38: Fish consumption  
 * - Index 38 → Code 39: Meat/eggs consumption
 * - Index 39 → Code 40: Grains consumption
 * - Index 40 → Code 41: Fruits/vegetables consumption
 * - Index 41 → Code 42: Dairy consumption
 
 *
 * Logic:
 * - autoFillValues: Pre-fills answers (still recorded for API submission)
 * - deactivateSlides: Slides are skipped and not shown to user
 * - activateSlides: Slides to show (38-42 only)
 */
export const DIET_NAVIGATION_CONFIG = {
  none: {
    activateSlides: [38, 39, 40, 41, 42], // Fish, meat, grains, fruits, dairy
  },
  // Vegan: Pre-fill fish/meat/dairy, then hide those questions
  vegan: {
    autoFillValues: [
      { slideCode: 38, key: 'eat-fish-per-week', value: '1', answerCode: 154 }, // Less than once
      { slideCode: 39, key: 'eat-meat-per-week', value: '1', answerCode: 157 }, // Less than once
      { slideCode: 42, key: 'dairy-per-day', value: '1', answerCode: 167 }, // None
    ],
    deactivateSlides: [38, 39, 42],
    activateSlides: [40, 41], // Grains, fruits
  },
  // Vegetarian: Pre-fill fish, then hide that question
  vegetarian: {
    autoFillValues: [
      { slideCode: 38, key: 'eat-fish-per-week', value: '1', answerCode: 154 }, // Less than once
    ],
    deactivateSlides: [38],
    activateSlides: [39, 40, 41, 42],
  },
  // Dairy-free: Pre-fill dairy, then hide that question
  dairyFree: {
    autoFillValues: [
      { slideCode: 42, key: 'dairy-per-day', value: '1', answerCode: 167 }, // None dairy
    ],
    deactivateSlides: [42],
    activateSlides: [38, 39, 40, 41],
  },
  // Gluten-free: Pre-fill grains, then hide that question
  glutenFree: {
    autoFillValues: [
      { slideCode: 40, key: 'eat-grains-per-day', value: '1', answerCode: 160 }, // Less than once
    ],
    deactivateSlides: [40],
    activateSlides: [38, 39, 41, 42],
  },
  // Other: No specific diet, show all diet questions (same as none)
  other: {
    activateSlides: [38, 39, 40, 41, 42], // Fish, meat, grains, fruits, dairy
  },
};
